/**
 * Browser-based Location Service for MMS Web
 * Provides geolocation tracking using the browser's Geolocation API
 */

class LocationService {
  constructor() {
    this.watchId = null;
    this.isTracking = false;
    this.lastPosition = null;
    this.callbacks = {
      success: null,
      error: null
    };
  }

  /**
   * Request location permission from browser
   */
  async requestPermission() {
    try {
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by your browser');
      }

      // Try to get current position to trigger permission prompt
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          () => {
            console.log('✅ Location permission granted');
            resolve(true);
          },
          (error) => {
            console.error('❌ Location permission denied:', error.message);
            reject(new Error('Location permission denied'));
          }
        );
      });
    } catch (error) {
      console.error('Error requesting location permission:', error);
      throw error;
    }
  }

  /**
   * Get current location once
   */
  async getCurrentLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.lastPosition = position;
          resolve(position);
        },
        (error) => {
          reject(this.handleGeolocationError(error));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  }

  /**
   * Start continuous location tracking
   * @param {Function} successCallback - Called when location is updated
   * @param {Function} errorCallback - Called on error
   * @param {Number} interval - Update interval in milliseconds (default: 5 minutes)
   */
  startTracking(successCallback, errorCallback, interval = 300000) {
    if (this.isTracking) {
      console.warn('Location tracking is already active');
      return;
    }

    if (!navigator.geolocation) {
      const error = new Error('Geolocation is not supported');
      if (errorCallback) errorCallback(error);
      return;
    }

    this.callbacks.success = successCallback;
    this.callbacks.error = errorCallback;

    // Use watchPosition for continuous tracking
    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.lastPosition = position;
        this.isTracking = true;
        
        console.log('📍 Location updated:', {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        });

        if (this.callbacks.success) {
          this.callbacks.success(position);
        }
      },
      (error) => {
        console.error('Location tracking error:', error);
        if (this.callbacks.error) {
          this.callbacks.error(this.handleGeolocationError(error));
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0
      }
    );

    // Also set up periodic updates (fallback for browsers that don't update frequently)
    this.intervalId = setInterval(async () => {
      try {
        const position = await this.getCurrentLocation();
        if (this.callbacks.success) {
          this.callbacks.success(position);
        }
      } catch (error) {
        console.error('Periodic location update error:', error);
      }
    }, interval);

    console.log('✅ Location tracking started');
    this.isTracking = true;
  }

  /**
   * Stop location tracking
   */
  stopTracking() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.isTracking = false;
    this.callbacks = {
      success: null,
      error: null
    };

    console.log('🛑 Location tracking stopped');
  }

  /**
   * Calculate distance between two points using Haversine formula
   * @param {Number} lat1 - Latitude of point 1
   * @param {Number} lon1 - Longitude of point 1
   * @param {Number} lat2 - Latitude of point 2
   * @param {Number} lon2 - Longitude of point 2
   * @returns {Number} Distance in meters
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371000; // Earth's radius in meters
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
      Math.cos(this.toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    return distance;
  }

  /**
   * Check if current location is near a job site
   * @param {Number} jobLat - Job site latitude
   * @param {Number} jobLng - Job site longitude
   * @param {Number} threshold - Distance threshold in meters (default: 100m)
   * @returns {Object} { atSite: boolean, distance: number }
   */
  async checkIfAtJobSite(jobLat, jobLng, threshold = 100) {
    try {
      const position = await this.getCurrentLocation();
      const distance = this.calculateDistance(
        position.coords.latitude,
        position.coords.longitude,
        jobLat,
        jobLng
      );

      return {
        atSite: distance <= threshold,
        distance: distance
      };
    } catch (error) {
      console.error('Error checking job site proximity:', error);
      throw error;
    }
  }

  /**
   * Open navigation to a location (Google Maps in new tab)
   * @param {Number} lat - Destination latitude
   * @param {Number} lng - Destination longitude
   * @param {String} label - Location label
   */
  navigateToJobSite(lat, lng, label = 'Job Site') {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&destination_name=${encodeURIComponent(label)}`;
    window.open(url, '_blank');
  }

  /**
   * Format distance for display
   * @param {Number} meters - Distance in meters
   * @returns {String} Formatted distance string
   */
  formatDistance(meters) {
    if (meters < 1000) {
      return `${Math.round(meters)}m`;
    } else {
      return `${(meters / 1000).toFixed(1)}km`;
    }
  }

  /**
   * Convert degrees to radians
   */
  toRadians(degrees) {
    return degrees * (Math.PI / 180);
  }

  /**
   * Handle geolocation errors
   */
  handleGeolocationError(error) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return new Error('Location permission denied. Please enable location access in your browser settings.');
      case error.POSITION_UNAVAILABLE:
        return new Error('Location information is unavailable.');
      case error.TIMEOUT:
        return new Error('Location request timed out.');
      default:
        return new Error('An unknown error occurred while getting location.');
    }
  }

  /**
   * Get tracking status
   */
  getTrackingStatus() {
    return {
      isTracking: this.isTracking,
      lastPosition: this.lastPosition
    };
  }
}

// Export singleton instance
export const locationService = new LocationService();
